import React, { useState } from 'react'
import { Lightbulb } from 'lucide-react'
import { useGetCreativeIdeasQuery } from '@/redux/collaborationsApi'
import { Eyebrow, Pill, fieldCls, linkCls, primaryCls } from './ui'

/** The text of a concept submission, which may be JSON {fields:[{value}]} or plain text */
export const conceptText = (s: any): string => {
  const raw = s?.contentDescription
  if (!raw) return ''
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed?.fields))
      return parsed.fields
        .map((f: any) => f.value)
        .filter(Boolean)
        .join('\n\n')
  } catch {
    /* plain text */
  }
  return String(raw)
}

const latestOf = (m: any) =>
  [...(m?.submissions ?? [])].sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0] ?? null

/**
 * Creative concept: what the athlete plans to make, in a few lines. Shows the brand's answer
 * (approved, or what to change) and lets the athlete send a new version.
 */
export const ConceptStep: React.FC<{
  dealId: string
  milestone: any
  brandName: string
  busy: boolean
  onSubmit: (text: string) => Promise<unknown>
}> = ({ dealId, milestone, brandName, busy, onSubmit }) => {
  const latest = latestOf(milestone)
  const changes = latest?.status === 'revision_requested'
  const pending = latest?.status === 'pending_review'
  const approved = latest?.status === 'approved' || milestone.status === 'approved'
  const [text, setText] = useState(changes ? conceptText(latest) : '')
  const [ideasOpen, setIdeasOpen] = useState(false)
  const { data: ideas } = useGetCreativeIdeasQuery(dealId, { skip: !ideasOpen })

  const feedback =
    typeof latest?.reviewNotes === 'string'
      ? latest.reviewNotes
      : (latest?.reviewNotes?.additionalComments ?? latest?.reviewNotes?.comment ?? null)

  if (approved) {
    return (
      <div className="mt-3 space-y-2">
        <Pill tone="green">Approved</Pill>
        <p className="whitespace-pre-wrap text-sm text-foreground">{conceptText(latest)}</p>
      </div>
    )
  }

  if (pending) {
    return (
      <div className="mt-3 space-y-2">
        <Pill tone="blue">With {brandName}</Pill>
        <p className="whitespace-pre-wrap text-sm text-foreground">{conceptText(latest)}</p>
        <p className="text-xs text-muted-foreground">
          Version {latest.version}. You'll hear back here.
        </p>
      </div>
    )
  }

  return (
    <div className="mt-3 space-y-3">
      {changes ? (
        <div className="rounded-xl border border-[#FCD9A8] bg-[#FFF4E5] p-3">
          <Eyebrow tone="amber">{brandName} asked for changes</Eyebrow>
          <p className="mt-1 text-sm text-foreground">{feedback || 'See their notes below.'}</p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          A few lines on what you'll make: the setting, the hook, where the product shows up.
        </p>
      )}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Open on a 5am alarm, cut to the serving drill in the kit, end on the product and the hashtag…"
        className={`${fieldCls} min-h-32 resize-none`}
      />
      <div className="flex items-center justify-between">
        <button type="button" className={linkCls} onClick={() => setIdeasOpen((o) => !o)}>
          <Lightbulb className="mr-1 inline h-4 w-4" /> Need ideas?
        </button>
        <span className="text-xs text-muted-foreground">{text.trim().length} characters</span>
      </div>
      {ideasOpen ? (
        <ul className="space-y-2">
          {(Array.isArray(ideas) ? ideas : ((ideas as any)?.ideas ?? []))
            .slice(0, 4)
            .map((idea: any, i: number) => {
              const body =
                typeof idea === 'string'
                  ? idea
                  : (idea.content?.concept ??
                    idea.content?.integration_concept ??
                    idea.content?.theme ??
                    idea.title)
              return (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => setText(body)}
                    className="w-full rounded-xl border border-border bg-muted p-3 text-left text-sm text-foreground hover:border-[#C0FD72]"
                  >
                    {body}
                  </button>
                </li>
              )
            })}
          {!ideas ? <li className="text-sm text-muted-foreground">Thinking…</li> : null}
        </ul>
      ) : null}
      <button
        type="button"
        className={primaryCls}
        disabled={busy || text.trim().length < 20}
        onClick={() => onSubmit(text.trim())}
      >
        {changes ? 'Send the new version' : 'Send my concept'}
      </button>
    </div>
  )
}

export default ConceptStep
